// ============================================
// AUTH STORE
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Company } from '@crew-at-sea/shared-types';

interface User {
  id: string;
  email: string;
  role: 'COMPANY';
  company?: Company;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      login: (user, accessToken, refreshToken) => {
        const domain = window.location.hostname.includes('crewatsea.com') ? 'domain=.crewatsea.com;' : '';
        document.cookie = `crewatsea_role=${user.role}; ${domain} path=/; max-age=31536000`;
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        const domain = window.location.hostname.includes('crewatsea.com') ? 'domain=.crewatsea.com;' : '';
        document.cookie = `crewatsea_role=; ${domain} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'company-auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
